import type { Me } from '@cliqcrm/contracts';

/**
 * Um conjunto de papéis com o Super Admin junto (ADR-0025): na matriz ele tem
 * tudo, e nas automações monta os dois tipos. Todo conjunto de "quem pode" da
 * tela nasce por aqui, para nenhum esquecer dele.
 */
export function comSuperAdmin(papeis: readonly string[]): ReadonlySet<string> {
  return new Set([...papeis, 'super_admin']);
}

/**
 * Quem vê a área de relatórios: gestor e administrador da organização, que são
 * os papéis com `indicador` na matriz. A API recusa os outros do mesmo jeito; o
 * menu só não oferece uma porta que diria não.
 */
export function papelVeRelatorios(papel: string | undefined): boolean {
  return papel === 'gestor' || papel === 'admin_tenant' || papel === 'super_admin';
}

/**
 * Quem gere organizações (criar, completar, encerrar, reabrir e excluir): só o
 * Super Admin (emendas de 21/10 e de 26/10 ao ADR-0025). A operação e o
 * administrador do suporte faziam parte disso até ali. Espelha
 * `exigirGestaoDeOrganizacoes` da API, que recusa os outros do mesmo jeito.
 */
export function podeCriarOrganizacaoComo(me: Me | null): boolean {
  return me?.papel === 'super_admin';
}

/** Quem dá e tira o papel de Super Admin: só outro Super Admin (ADR-0025). */
export function geraSuperAdmins(papel: string | null | undefined): boolean {
  return papel === 'super_admin';
}

/** Os papéis com `cliente × editar` em `@cliqcrm/modulo-acesso` (matriz §13.2). */
export const EDITA_CLIENTE: ReadonlySet<string> = comSuperAdmin([
  'vendedor',
  'gestor',
  'admin_tenant',
]);

/**
 * Os papéis com `oportunidade × editar` (matriz §13.2): quem escolhe e marca a
 * consulta, no card da oportunidade e no Cliente 360.
 */
export const EDITA_OPORTUNIDADE: ReadonlySet<string> = comSuperAdmin([
  'vendedor',
  'gestor',
  'admin_tenant',
]);

/**
 * Os papéis com `oportunidade × ler` (matriz §13.2): quem assina o aviso do funil
 * (`funil:eu`). Assinar sem poder ler recusa o fluxo inteiro.
 */
export const LE_OPORTUNIDADE: ReadonlySet<string> = comSuperAdmin([
  'vendedor',
  'gestor',
  'admin_tenant',
]);

/**
 * Quem enxerga integração, espelhando `integracao × ler` da matriz (§13.2).
 *
 * O gestor NÃO está aqui, e é intencional: a §13.2 dá integração ao
 * administrador da organização e à operação da Cliqfy. Um gestor que abrisse
 * Conexões receberia a recusa da API em cima de uma tela que o convidou a
 * entrar — o pior tipo de porta, a que existe só para dizer não.
 *
 * ESCONDE, não desabilita, pela mesma razão das ações da lista de clientes: o
 * papel não muda ao longo do dia, e um item permanentemente apagado é ruído
 * para sempre.
 *
 * Isto é conveniência de tela, e não segurança. Quem chamar
 * `/v1/admin/integrations` direto continua sendo recusado pela API — a matriz
 * é a que manda, e ela já mandava antes desta lista existir.
 */
export const VE_INTEGRACAO: ReadonlySet<string> = comSuperAdmin([
  'admin_tenant',
  'operacao_cliqfy',
]);

/**
 * Quem liga e desliga funcionalidade e portal na loja: `configuracao × editar`
 * da matriz (§13.2), que é a guarda de `PATCH /v1/admin/flags/:chave` e que só
 * o administrador da organização tem.
 */
export const EDITA_FUNCIONALIDADES: ReadonlySet<string> = comSuperAdmin(['admin_tenant']);

/**
 * Quem aperta "Atualizar agora" nos preços da clínica: `configuracao × editar`,
 * a guarda de `POST /v1/consultas/precos/sincronizar` (#1084). Ler os preços é
 * de quem lê cliente; atualizar relê o sistema da clínica, e é da administração.
 */
export const ATUALIZA_PRECOS_DA_CLINICA: ReadonlySet<string> = comSuperAdmin(['admin_tenant']);

/**
 * Quem adiciona canal: `integracao × editar`, a guarda das rotas que criam
 * canal (`canais-simples`, `canal-whatsapp`, `meta-oauth`). A Operação Cliqfy
 * tem, e Conexões já lhe oferece "Adicionar canal": a loja não pode dizer o
 * contrário.
 */
export const CONECTA_CANAIS: ReadonlySet<string> = comSuperAdmin([
  'admin_tenant',
  'operacao_cliqfy',
]);

/**
 * Quem vê as automações: `automacao × ler` da matriz (ADR-0021). O gestor cria,
 * edita e envia para homologação; ligar é do administrador (`ATIVA_AUTOMACAO`).
 */
export const VE_AUTOMACOES: ReadonlySet<string> = comSuperAdmin([
  'gestor',
  'admin_tenant',
  'operacao_cliqfy',
]);

/** Quem edita automações (e cancela uma espera): `automacao × editar` (ADR-0021). */
export const EDITA_AUTOMACOES: ReadonlySet<string> = comSuperAdmin([
  'gestor',
  'admin_tenant',
  'operacao_cliqfy',
]);

/** Quem põe uma automação no ar: `automacao × ativar` (ADR-0021). */
export const ATIVA_AUTOMACAO: ReadonlySet<string> = comSuperAdmin([
  'admin_tenant',
  'operacao_cliqfy',
]);

/**
 * Quem arquiva e desarquiva uma automação: `automacao × remover` (ADR-0021).
 * O gestor cria e edita, mas não tira uma automação de uso.
 */
export const ARQUIVA_AUTOMACAO: ReadonlySet<string> = comSuperAdmin([
  'admin_tenant',
  'operacao_cliqfy',
]);

/**
 * Quem monta automação com SQL (os blocos `consulta_sql` e `para_cada`): só a
 * Cliqfy, que tem `integracao_de_dados` (decisão de 19/10, #932). A empresa monta
 * as internas.
 */
export const MONTA_AUTOMACAO_COM_SQL: ReadonlySet<string> = comSuperAdmin(['operacao_cliqfy']);

/**
 * Quem monta automação interna (sem SQL): só a empresa, o dono e o gestor
 * (decisão de 19/10, #932). A Cliqfy monta só as com SQL.
 */
export const MONTA_AUTOMACAO_INTERNA: ReadonlySet<string> = comSuperAdmin([
  'gestor',
  'admin_tenant',
]);

/**
 * Quem vê as conexões aos bancos dos clientes e as operações SQL:
 * `integracao_de_dados × ler`. Desde a revisão de 19/10 do ADR-0021, só os
 * devs da Cliqfy.
 */
export const VE_INTEGRACAO_DE_DADOS: ReadonlySet<string> = comSuperAdmin(['operacao_cliqfy']);

/** Quem pede uma lista nova: `lista × criar` (revisão de 19/10 do ADR-0021). */
export const PEDE_LISTA: ReadonlySet<string> = comSuperAdmin(['gestor', 'admin_tenant']);

/** Quem monta, publica e arquiva listas e atende os pedidos: `lista × editar`. */
export const MONTA_LISTA: ReadonlySet<string> = comSuperAdmin(['operacao_cliqfy']);

/**
 * Quem cria, renomeia, reordena e arquiva os campos personalizados:
 * `campo_personalizado × editar` (ADR-0023). Os outros papéis só preenchem.
 */
export const MEXE_EM_CAMPOS: ReadonlySet<string> = comSuperAdmin(['gestor', 'admin_tenant']);

/**
 * Quem escolhe as rotinas do "Atualizar" de cada funil: só o Super Admin
 * (decisão de 28/10, ADR-0030). Espelha `exigirSuperAdminEmVigor` da API.
 */
export function configuraOAtualizar(papel: string | null): boolean {
  return papel === 'super_admin';
}
